import {Button} from 'platform/components';
import {Heading, HStack} from 'platform/foundation';

import {head, isEmpty} from 'ramda';

import {useSourcingListUserCarvagoBuyQuery} from '@dms/api/core';
import i18n from '@dms/i18n';
import {testIds} from '@dms/routes';
import {CURRENCY_CZK, COUNTRY_CZE, formatDocumentTitle} from '@dms/shared';

import {openInNewTab} from 'shared/web';

import {SourcingDataStatus} from '../../components/SourcingDataStatus/SourcingDataStatus';
import {SourcingLayout} from '../../components/SourcingLayout/SourcingLayout';
import {useSourcingTable} from '../../components/SourcingTable/hooks/useSourcingTable';
import {useSourcingTableColumns} from '../../components/SourcingTable/hooks/useSourcingTableColumns';
import {SourcingTable} from '../../components/SourcingTable/SourcingTable';
import {createSourcingTableQueryResultFromListQuery} from '../../components/SourcingTable/utils/createSourcingTableQueryResult';
import {VehiclePreview} from '../../components/VehiclePreview/VehiclePreview';
import {CARVAGO_TRANSACTION_WORKSPACE_URLS} from '../../constants/carvagoTransactionWorkspaceUrls';
import {
  CLASSIFIEDS_USER_LIST_ORDER_BY_MAP,
  CLASSIFIEDS_USER_LIST_SORT_OPTIONS,
} from '../../constants/classifiedsSortBy';
import {USER_LIST_DEFAULT_ORDER_ID} from '../../constants/USER_LIST_DEFAULT_ORDER_ID';
import {useSourcingCurrency} from '../../hooks/useSourcingCurrency';
import {useSourcingTargetCountry} from '../../hooks/useSourcingTargetCountry';
import {getSourcingVehicleId} from '../../utils/getSourcingVehicleId';

export function UserCarvagoBuyPage() {
  const currency = useSourcingCurrency();
  const targetCountry = useSourcingTargetCountry();
  const [state, dispatch] = useSourcingTable('user-carvago-buy', USER_LIST_DEFAULT_ORDER_ID);

  const orderBy = CLASSIFIEDS_USER_LIST_ORDER_BY_MAP.get(state.orderBy);

  const listQueryResult = useSourcingListUserCarvagoBuyQuery({
    orderBy: isEmpty(orderBy) ? undefined : orderBy?.join(','),
    currency: currency?.code ?? CURRENCY_CZK,
    buyerCountry: targetCountry?.code ?? COUNTRY_CZE,
    size: 1000,
  });

  const columns = useSourcingTableColumns();
  const tableQueryResult = createSourcingTableQueryResultFromListQuery(listQueryResult);
  const rows = tableQueryResult.data;

  const selectedAdId = state.selectedRow?.sourcingVehicle.adId;
  const preview = rows.find((row) => row.sourcingVehicle.adId === selectedAdId) ?? head(rows);

  const handleManageRequests = () => {
    const url =
      process.env.ENV_TYPE === 'prod'
        ? CARVAGO_TRANSACTION_WORKSPACE_URLS.prod
        : CARVAGO_TRANSACTION_WORKSPACE_URLS.other;

    openInNewTab(url);
  };

  return (
    <SourcingLayout
      header={
        <HStack align="center" justify="space-between">
          <Heading size={2} alternative>
            {i18n.t('entity.vehicle.labels.carvagoBuyPage')}
          </Heading>
          <Button
            title={i18n.t('entity.sourcing.actions.manageRequests')}
            onClick={handleManageRequests}
            data-testid={testIds.sourcing.classifieds('carvagoBuy-manageRequestsButton')}
          />
        </HStack>
      }
      content={
        <SourcingDataStatus
          isEmpty={!tableQueryResult.isLoading && isEmpty(rows)}
          iconUrl="/assets/images/empty_images/empty-basket.svg"
          text={i18n.t('entity.vehicle.notifications.nothingInCarvagoBuy')}
        >
          <SourcingTable
            state={state}
            dispatch={dispatch}
            columns={columns}
            sortByOptions={CLASSIFIEDS_USER_LIST_SORT_OPTIONS}
            queryResult={tableQueryResult}
          />
        </SourcingDataStatus>
      }
      iframe={
        <VehiclePreview
          key={getSourcingVehicleId(preview)}
          vehicleId={getSourcingVehicleId(preview)}
        />
      }
      pageTitle={formatDocumentTitle(
        i18n.t('entity.vehicle.labels.carvagoBuyPage'),
        i18n.t('page.sourcing.labels.classifieds'),
        i18n.t('page.sourcing.labels.sourcing')
      )}
    />
  );
}
